import type {
  AssignmentEntity,
  AssignmentInputEntity
} from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { AssignmentSchema } from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree'
import { AssignedModule } from './AssignedModule'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'

/**
 * Classe représentant une modularisation (assignment) d'une période scolaire
 */
export class Assignment {
  readonly id: number
  readonly schoolPeriodId: number
  readonly degree: ClassroomDegree.Values
  readonly locked: boolean
  readonly assignedModules: AssignedModule[]

  constructor(assignment: AssignmentEntity) {
    this.id = assignment.id
    this.schoolPeriodId = assignment.schoolPeriodId
    this.degree = assignment.degree
    this.locked = assignment.locked
    this.assignedModules = assignment.assignedModules.map((module) => AssignedModule.create(module))
  }

  /**
   * Vérifie si l'assignment contient un module d'apprentissage spécifique
   */
  public hasLearningModule(learningModuleId: number): boolean {
    return this.assignedModules.some((module) => module.learningModuleId === learningModuleId)
  }

  /**
   * Obtient le libellé du degré
   */
  public getDegreeLabel(): string {
    return ClassroomDegree.label(this.degree)
  }

  /**
   * Vérifie si l'assignment est verrouillé
   */
  public isLocked(): boolean {
    return this.locked
  }

  /**
   * Convertit l'assignment en format d'entrée pour l'API
   */
  public toInput(): AssignmentInputEntity {
    return {
      id: this.id,
      schoolPeriodId: this.schoolPeriodId,
      degree: this.degree,
      locked: this.locked,
      assignedModules: this.assignedModules.map((module) => module.toInput())
    }
  }

  /**
   * Méthode de création statique qui valide les données avant instanciation
   */
  public static create(data: unknown): Assignment {
    return EntityValidator.validateAndCreate(AssignmentSchema, data, Assignment, 'Assignment')
  }
}
